import type {
  UppyFile as BaseUppyFile,
  MinimalRequiredUppyFile,
} from '@uppy/core'

import { MediaType, UploadStatus } from '../constants'
import type { S3Upload, Upload } from '../entities/Upload'
import {
  getFileExtension,
  getFileMimeType,
  getMediaTypeFromMime,
} from '../utils'
import type { LocalFile } from '../values/LocalFile'
import { UploadMetadata } from '../values/UploadMetadata'
import { UploadProgress } from '../values/UploadProgress'

export type S3Response = {
  location: string
  key: string
}

export type UploaderMeta = Record<string, unknown> & UploadMetadata
export type UploaderBody = Record<string, unknown>

export type UppyFile = BaseUppyFile<UploaderMeta, UploaderBody>
export type LocalUppyFile = Extract<UppyFile, { isRemote: false }>
export type SuccessResponse = NonNullable<UppyFile['response']>
export type UploadedUppyFile = UppyFile & {
  uploadURL: string
  response: SuccessResponse
}
export type FailedUppyFile = UppyFile & { error: string }
export type UppyFileDescriptor = MinimalRequiredUppyFile<
  UploaderMeta,
  UploaderBody
>
// Uppy leaves both buckets optional and its own upload() resolves to undefined
// when nothing ran. Uploader.upload settles that here so callers can index in.
export type UploadResult = {
  successful: UploadedUppyFile[]
  failed: UppyFile[]
}

export const UppyFile = {
  isUploadedUppyFile(file: UppyFile): file is UploadedUppyFile {
    return 'uploadURL' in file
  },

  isFailedUppyFile(file: UppyFile): file is FailedUppyFile {
    return 'error' in file
  },

  shouldGenerateThumbnail(file: UppyFile): boolean {
    if (file.preview) {
      return false
    }

    const mediaType = getMediaTypeFromMime(file.type)

    return (
      mediaType === MediaType.gif ||
      mediaType === MediaType.video ||
      mediaType === MediaType.document
    )
  },

  toLocalFile(file: UppyFile): LocalFile {
    if (!(file.data instanceof Blob)) {
      throw new Error('Cannot create LocalFile from a file without its data')
    }

    return {
      name: file.name,
      type: getFileMimeType(file),
      extension: getFileExtension(file.name),
      size: file.data.size,
      data: file.data,
      meta: UploadMetadata.new(file.meta),
    }
  },

  toUpload(file: UppyFile, status?: UploadStatus): Upload {
    const isCompleted = status === UploadStatus.Completed
    const isCompletedUppyFile = UppyFile.isUploadedUppyFile(file)
    if (isCompleted && !isCompletedUppyFile) {
      throw new Error('Cannot create completed upload from non-uploaded file')
    }

    const mimeType = getFileMimeType(file)

    return {
      id: file.id,
      name: file.name,
      status: status || UploadStatus.Added,
      type: mimeType,
      extension: getFileExtension(file.name),
      mediaType: getMediaTypeFromMime(mimeType),
      uploaderId: file.meta.uploaderId,
      thumbnailUrl: file.preview,
      progress: UploadProgress.new(
        file.progress?.bytesTotal,
        file.progress?.bytesUploaded,
      ),
      url: isCompleted && isCompletedUppyFile ? file.uploadURL : undefined,
      size: file.size ?? 0,
      alt: '',
      meta: UploadMetadata.new(file.meta),
    }
  },

  mapToS3Upload(files: UploadedUppyFile[]): S3Upload[] {
    return files.filter((f) => !!f).map(UppyFile.toS3Upload)
  },

  toS3Upload(file: UploadedUppyFile): S3Upload {
    const responseBody = file.response?.body as S3Response | undefined

    if (!responseBody) {
      throw new Error('Cannot create S3Upload: file.response.body is undefined')
    }

    const { location } = responseBody
    const { key } = file.meta

    if (!location || !key) {
      throw new Error('Cannot create S3Upload without required data')
    }

    return {
      ...UppyFile.toUpload(file),
      status: UploadStatus.UploadedToS3,
      url: location,
      key,
    }
  },

  toCompletedUpload(file: UploadedUppyFile): Upload {
    return UppyFile.toUpload(file, UploadStatus.Completed)
  },

  toFailedUpload(file: UppyFile, error: string): Upload {
    return {
      ...UppyFile.toUpload(file),
      status: UploadStatus.Failed,
      error,
    }
  },
}
